import React from "react";
import type { BaseEntity } from "../../UniversalCRMTable/types";
import type { UniversalEntityPageProps } from "../types";
import type { UniversalEntityPageContextValue } from "../contexts/UniversalEntityPageContext";

// Import composition hooks
import {
  useCompositionAuth,
  useCompositionViewMode,
  useCompositionPagination,
  useCompositionDataFetching,
  useCompositionHandlers,
  useCompositionDetailModal,
  useCompositionBulkActions,
  useCompositionCallbacks,
  useCompositionSavedViews,
  useCompositionContextValue,
} from "./composition";

/**
 * Composition hook that orchestrates all UniversalEntityPage hooks.
 * Returns a context-ready value object with all state and handlers.
 *
 * This hook centralizes hook orchestration and reduces the main component
 * to a thin orchestrator that just uses this hook and renders with context.
 *
 * Focused context slices live in useUniversalEntityPageFocusedContexts.
 */
export function useUniversalEntityPageComposition<T extends BaseEntity>({
  entityName,
  config,
  hooks,
  filterPreset = "all",
  customFilters = {},
  entityFormProps,
  customComponents = {},
  bulkActionMutations,
  enableFTS = false,
}: UniversalEntityPageProps<T>): UniversalEntityPageContextValue<T> {
  // ============================================================================
  // 1. AUTH AND PERMISSIONS
  // ============================================================================
  const auth = useCompositionAuth(entityName, {
    entityName,
    config,
    hooks,
    filterPreset,
    customFilters,
    entityFormProps,
    customComponents,
    bulkActionMutations,
    enableFTS,
  });

  const {
    appUser,
    isAuthenticated,
    isClientUser,
    authLoading,
    isEmployee,
    isAdmin,
    isWTCCRM,
    isWTCEmployee,
    isAdminRole,
    isMobile,
    canCreate,
  } = auth;

  // ============================================================================
  // 1.5 VIEW MODE STATE (TABLE, GROUPED, OR CARDS)
  // ============================================================================
  const { viewMode, setViewMode } = useCompositionViewMode(entityName);

  // ============================================================================
  // 1.6 UNIFIED PAGINATION MODE STATE (INLINE MANAGEMENT)
  // ============================================================================
  const paginationModeState = useCompositionPagination(entityName, config.defaultPageSize);

  // ============================================================================
  // 1.7 EFFECTIVE PAGINATION — override pageSize for board view
  // ============================================================================
  const effectivePaginationModeState = React.useMemo(() => {
    if (viewMode === "board" && config.boardViewPageSize) {
      return {
        ...paginationModeState,
        paginationMode: {
          ...paginationModeState.paginationMode,
          pageSize: config.boardViewPageSize,
        },
      };
    }
    return paginationModeState;
  }, [viewMode, paginationModeState, config.boardViewPageSize]);

  // ============================================================================
  // 2. DATA FETCHING AND PROCESSING
  // ============================================================================
  const {
    data,
    navigation,
    entityState,
    filterState,
    paginationState,
    dataState,
    mutations,
    normalizedCurrentFilters,
  } = useCompositionDataFetching(
    entityName,
    config,
    hooks,
    filterPreset,
    customFilters,
    appUser,
    enableFTS,
    effectivePaginationModeState
  );

  // ============================================================================
  // 3. EVENT HANDLERS
  // ============================================================================
  const { handlers, handlePageChange, handlePageSizeChange } = useCompositionHandlers<T>(
    entityName,
    config,
    hooks,
    isMobile,
    canCreate,
    paginationModeState,
    dataState,
    entityState,
    filterState,
    normalizedCurrentFilters,
    data.filterHelpers
  );

  // ============================================================================
  // 4.1 DETAIL MODAL NAVIGATION
  // ============================================================================
  const { handleOpenDetailModal } = useCompositionDetailModal<T>(entityName, navigation);

  // ============================================================================
  // 4. BULK ACTIONS (MUST BE BEFORE CALLBACKS)
  // ============================================================================
  const { bulkActions, configWithEnhancedBulkActions } = useCompositionBulkActions(
    entityName,
    normalizedCurrentFilters,
    bulkActionMutations,
    entityState.setBulkEmailExportData,
    config,
    hooks
  );

  // ============================================================================
  // 5. MEMOIZED CALLBACKS (USES ENHANCED CONFIG)
  // ============================================================================
  const {
    callbacks,
    handleGroupingExport,
    renderSimpleTable,
  } = useCompositionCallbacks<T>(
    entityName,
    configWithEnhancedBulkActions,
    dataState,
    filterState,
    paginationModeState,
    handlers.handleEditItem,
    handlers.handleViewItem,
    handlers.handleInlineEdit,
    handlePageChange,
    handlePageSizeChange,
    handlers.handleExportWithScope,
    handlers.handleSavedViewsReady,
    handleOpenDetailModal,
    hooks,
    data.filterHelpers
  );

  // Add the detail-modal handler — `useCompositionHandlers` returns the rest
  // of the Handlers<T> contract (no orchestration internals leak here).
  const handlersWithDetailModal = React.useMemo(
    () => ({
      ...handlers,
      handleOpenDetailModal,
    }),
    [handlers, handleOpenDetailModal]
  );

  // ============================================================================
  // 6. SAVED VIEWS STATE
  // ============================================================================
  const { savedViewsState } = useCompositionSavedViews(
    entityName,
    configWithEnhancedBulkActions.savedViews || [],
    filterState.serializeFilters,
    filterState.applyFilters,
    data.navigation.activeViewId,
    filterState.setActiveViewId
  );

  // ============================================================================
  // 7. RETURN CONTEXT VALUE
  // ============================================================================
  const contextValue = useCompositionContextValue<T>({
    entityName,
    config: configWithEnhancedBulkActions,
    hooks,
    entityFormProps,
    customComponents,
    appUser,
    isAuthenticated,
    isClientUser,
    authLoading,
    isEmployee,
    isAdmin,
    isWTCCRM,
    isWTCEmployee,
    isAdminRole,
    isMobile,
    canCreate,
    navigation,
    dataState,
    entityState,
    filterState,
    handlers: handlersWithDetailModal,
    callbacks,
    mutations,
    bulkActions,
    bulkActionMutations,
    savedViewsState,
    normalizedCurrentFilters,
    viewMode,
    setViewMode,
    paginationModeState,
  });

  return contextValue;
}
